"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import React from "react"

/**
 * A Next.js <Link /> for internal app routes. Named for history: this used to prepend the
 * region's country code (/au) to every href when Elevate's URLs showed it. Since the 21 Sep
 * 2026 domain cutover, URLs are clean (middleware rewrites /au internally, invisible to the
 * browser), so this just passes href straight through -- kept as one call site so a future
 * multi-region need has a single place to reintroduce a prefix.
 */
// Prefetch on intent, not on viewport (1 Oct 2026, Lighthouse 100 push, same as BOO): Next prefetched every visible
// <Link> right after load (3-7 RSC fetches per page) and the router processing them was a large mobile long task.
// Links now prefetch on hover / touchstart / focus, still ahead of the click. An explicit prefetch prop opts out.
const LocalizedClientLink = ({
  children,
  href,
  prefetch,
  onMouseEnter,
  onTouchStart,
  onFocus,
  ...props
}: {
  children?: React.ReactNode
  href: string
  className?: string
  "aria-label"?: string
  "aria-current"?: "page" | undefined
  onClick?: () => void
  passHref?: true
  [x: string]: any
}) => {
  const router = useRouter()
  const onIntent = prefetch === undefined ? () => router.prefetch(href) : undefined
  return (
    <Link
      href={href}
      prefetch={prefetch ?? false}
      onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
        onIntent?.()
        onMouseEnter?.(e)
      }}
      onTouchStart={(e: React.TouchEvent<HTMLAnchorElement>) => {
        onIntent?.()
        onTouchStart?.(e)
      }}
      onFocus={(e: React.FocusEvent<HTMLAnchorElement>) => {
        onIntent?.()
        onFocus?.(e)
      }}
      {...props}
    >
      {children}
    </Link>
  )
}

export default LocalizedClientLink
